import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Upload, Radio, Bell, Trophy, Target, Flame, LineChart, ShieldCheck, ArrowRight, Zap
} from "lucide-react";

export default function WelcomePage() {
  return (
    <div className="min-h-screen bg-[#0A0D14] text-white overflow-x-hidden">
      <TopBar />
      <Hero />
      <FeatureStrip />
      <MarketShowcase />
      <LiveFlow />
      <DiscordBand />
      <FinalCTA />
      <Footer />
    </div>
  );
}

/* ---------- Shared bits ---------- */
function TopBar() {
  return (
    <header className="sticky top-0 z-40 bg-[#0A0D14]/85 backdrop-blur-md border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/welcome" className="flex items-center gap-2" data-testid="welcome-brand-link">
          <img src="/bettrackr-logo.svg" alt="BetTrackr" className="h-8 w-8" />
          <span className="font-black uppercase tracking-tight text-white text-lg" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Bet<span className="text-emerald-400">Trackr</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/login" data-testid="welcome-login-link">
            <Button variant="ghost" className="text-slate-200 hover:text-white hover:bg-white/5">Log in</Button>
          </Link>
          <Link to="/login" data-testid="welcome-signup-cta">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider">
              Get started
            </Button>
          </Link>
        </div>
      </div>
    </header>
  );
}

function Pill({ children, testid }) {
  return (
    <span
      data-testid={testid}
      className="inline-flex items-center gap-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-[10px] uppercase tracking-widest text-emerald-300 font-semibold"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
      {children}
    </span>
  );
}

/* ---------- Hero ---------- */
function Hero() {
  return (
    <section className="relative px-4 sm:px-6 pt-12 sm:pt-20 pb-16">
      <div
        className="absolute inset-0 opacity-25 pointer-events-none"
        style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1728659964407-ed9a942ec2de?crop=entropy&cs=srgb&fm=jpg&q=85')",
          backgroundSize: "cover", backgroundPosition: "center top",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D14]/40 via-[#0A0D14]/85 to-[#0A0D14]" />
      <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(rgba(16,185,129,0.08) 1px, transparent 1px)", backgroundSize: "28px 28px" }} />

      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
        <div>
          <Pill testid="welcome-hero-pill">Live NFL betting tracker</Pill>
          <h1
            data-testid="welcome-h1"
            className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tight leading-[0.95]"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            Snap the slip.<br />
            Watch every leg <span className="text-emerald-400">move live.</span>
          </h1>
          <p className="mt-5 text-slate-300 text-base sm:text-lg max-w-xl">
            BetTrackr turns a screenshot of your NFL bet into a live, auto-updating dashboard. Real ESPN
            data, Discord alerts, UK odds and GBP by default — ready for Sunday kickoff.
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link to="/login" data-testid="welcome-hero-cta">
              <Button className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider h-11 px-6">
                Start tracking free <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
            <a href="#features" className="text-xs uppercase tracking-widest text-slate-300 hover:text-white">
              See every market →
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-4 text-[11px] uppercase tracking-widest text-slate-400">
            <span className="flex items-center gap-2"><Radio className="h-3.5 w-3.5 text-emerald-400" /> Live ESPN feed</span>
            <span className="flex items-center gap-2"><Bell className="h-3.5 w-3.5 text-emerald-400" /> Discord alerts</span>
            <span className="flex items-center gap-2"><ShieldCheck className="h-3.5 w-3.5 text-emerald-400" /> UK odds / GBP</span>
          </div>
        </div>

        {/* Mock live slip card */}
        <div className="relative">
          <div className="absolute -inset-6 bg-emerald-500/10 blur-3xl rounded-[40px]" />
          <MockSlipCard />
        </div>
      </div>
    </section>
  );
}

function MockSlipCard() {
  return (
    <div className="relative rounded-2xl bg-[#121824] border border-white/10 shadow-2xl p-5 sm:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">FanDuel</div>
          <div className="text-xl font-black uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Parlay · 3 Legs
          </div>
        </div>
        <span className="rounded-md border border-emerald-500/30 bg-emerald-500/15 text-emerald-300 text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse mr-1.5" />Winning
        </span>
      </div>
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[["Odds", "6.42"], ["Stake", "£25.00"], ["Returns", "£160.50"]].map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div>
            <div className="font-mono font-bold text-white">{v}</div>
          </div>
        ))}
      </div>
      <div className="space-y-2">
        <MockLeg
          teams="KC @ MIA" pick="T. Kelce Over 50 Rec Yds" odds="1.83"
          detail="Q2 · 8:14" progress={{ val: 48, line: 50, pct: 96 }} status="on"
        />
        <MockLeg
          teams="SEA @ WSH" pick="J. Croskey-Merritt Anytime TD" odds="2.40"
          detail="Q3 · 11:52" liveNote="1 TD · HIT" status="won"
        />
        <MockLeg
          teams="PHI @ NYG" pick="Each team to score a FG" odds="1.67"
          detail="Q1 · 5:03" liveNote="PHI 1 FG · NYG 0 FG" status="on"
        />
      </div>
    </div>
  );
}

function MockLeg({ teams, pick, odds, detail, progress, liveNote, status }) {
  const badge = {
    won: { cls: "border-emerald-500/30 bg-emerald-500/15 text-emerald-300", label: "WON" },
    on:  { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", label: "ON TRACK" },
    off: { cls: "border-red-500/30 bg-red-500/10 text-red-300", label: "OFF TRACK" },
  }[status];
  return (
    <div className="rounded-lg bg-[#0E1420] border border-white/5 p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-mono text-slate-300">{teams}</span>
        <span className={`inline-flex items-center gap-1.5 rounded-md border font-bold uppercase tracking-wider text-[10px] px-2 py-1 ${badge.cls}`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />{badge.label}
        </span>
      </div>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-sm font-semibold text-white">{pick}</div>
        <div className="font-mono text-sm text-emerald-300">{odds}</div>
      </div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">{detail}</div>
      {progress ? (
        <>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="font-mono text-slate-400">{progress.val} / {progress.line}</span>
            <span className="text-slate-500">{progress.pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${progress.pct}%` }} />
          </div>
        </>
      ) : (
        <div className="text-xs text-slate-400 font-mono">{liveNote}</div>
      )}
    </div>
  );
}

/* ---------- Feature strip ---------- */
function FeatureStrip() {
  const feats = [
    { Icon: Upload, title: "Snap your slip", body: "Drop one or many screenshots. GPT-5.6 vision extracts legs, odds, stake, payouts — merged, deduped and ready to review." },
    { Icon: Radio, title: "Live ESPN engine", body: "Every leg polls real-time game state and box-score stats so your dashboard moves exactly when the game does." },
    { Icon: Bell, title: "Discord alerts", body: "Hook up a webhook and get a ping the second a leg hits 50%, 75%, 90%, flips to lost, or cashes the whole slip." },
  ];
  return (
    <section className="px-4 sm:px-6 py-14 border-t border-white/5" id="features">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Why BetTrackr</h2>
        <h3 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight max-w-2xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Built for the Sunday ticket — not a spreadsheet.
        </h3>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          {feats.map(({ Icon, title, body }) => (
            <div key={title} className="rounded-xl bg-[#121824] border border-white/10 p-5 hover:border-emerald-500/40 transition-colors">
              <div className="h-9 w-9 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mb-3">
                <Icon className="h-4 w-4 text-emerald-300" />
              </div>
              <div className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</div>
              <p className="text-sm text-slate-400 mt-1">{body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Market showcase (team FG + anytime TD) ---------- */
function MarketShowcase() {
  return (
    <section className="px-4 sm:px-6 py-16 bg-[#0C111C] border-t border-white/5" id="markets">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">New this season</h2>
            <h3 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight max-w-2xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
              Team field goals <span className="text-emerald-400">+</span> Anytime touchdowns.
            </h3>
            <p className="mt-2 text-slate-400 max-w-2xl">
              The two markets casual bettors actually care about on game day — now tracked natively, in real time, with the same engine driving every other leg.
            </p>
          </div>
          <Pill testid="welcome-markets-pill">Live tracking · P4</Pill>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <MarketCard
            icon={<Target className="h-5 w-5" />}
            kicker="Team field goals"
            title="Each team to score — or team O/U totals"
            lines={[
              "Each team to kick a FG · 1.67",
              "Chiefs Over 1.5 FGs · 1.91",
              "Dolphins Under 2.5 FGs · 1.72",
            ]}
            footer="Counts only MADE field goals from live ESPN scoring plays. Locks to WON the moment the condition is unreachable to lose."
            progress={{ label: "KC 1 FG · MIA 1 FG · each ≥ 1", pct: 100, cap: "WON" }}
          />
          <MarketCard
            icon={<Flame className="h-5 w-5" />}
            kicker="Anytime touchdown"
            title="Rushing + receiving TDs, as one number"
            lines={[
              "T. Hill Anytime TD · 2.40",
              "I. Pacheco 2+ TDs · 5.00",
              "C. McCaffrey Anytime TD · 1.57",
            ]}
            footer="Robust player matching (handles 'T. Hill', 'Tyreek Hill Jr.'). Stat is pulled live from the box score — rushing + receiving TDs combined."
            progress={{ label: "Pacheco · 1 TD toward 2+", pct: 50, cap: "ON TRACK" }}
          />
        </div>
      </div>
    </section>
  );
}

function MarketCard({ icon, kicker, title, lines, footer, progress }) {
  return (
    <div className="rounded-2xl bg-[#121824] border border-white/10 p-6 hover:border-emerald-500/40 transition-colors">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-10 w-10 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300">
          {icon}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400">{kicker}</div>
          <div className="text-xl font-black uppercase tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            {title}
          </div>
        </div>
      </div>
      <ul className="divide-y divide-white/5 rounded-lg bg-[#0E1420] border border-white/5 mb-4">
        {lines.map((l) => (
          <li key={l} className="flex items-center justify-between px-3 py-2 text-sm">
            <span className="text-slate-200">{l.split(" · ")[0]}</span>
            <span className="font-mono text-emerald-300">{l.split(" · ")[1]}</span>
          </li>
        ))}
      </ul>
      <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
        <div className="flex items-center justify-between text-xs mb-1">
          <span className="text-slate-300">{progress.label}</span>
          <span className="font-bold uppercase tracking-widest text-[10px] text-emerald-300">{progress.cap}</span>
        </div>
        <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full bg-emerald-500" style={{ width: `${progress.pct}%` }} />
        </div>
      </div>
      <p className="text-xs text-slate-500 mt-3">{footer}</p>
    </div>
  );
}

/* ---------- Live flow ---------- */
function LiveFlow() {
  const steps = [
    { n: "01", title: "Upload one or many screenshots", body: "Multi-image OCR merges partial captures and dedupes overlapping legs so one messy screen-grab becomes a clean slip." },
    { n: "02", title: "Review & confirm", body: "Edit anything the AI got wrong, pick the right NFL game, lock it in. UK decimal odds + GBP by default; switch to fractional or American whenever." },
    { n: "03", title: "Watch it move live", body: "Each leg shows live score, player stat progress, ON TRACK or OFF TRACK status, with a progress bar that fills as the game plays out." },
    { n: "04", title: "Ping on every change", body: "Discord webhook alerts the moment a leg hits 50/75/90%, flips to WON or LOST, or when the whole slip cashes — with your display name baked in." },
  ];
  return (
    <section className="px-4 sm:px-6 py-16 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">How it plays out</h2>
        <h3 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight max-w-xl" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          From screenshot to Sunday scoreboard.
        </h3>
        <div className="mt-10 grid md:grid-cols-2 gap-4">
          {steps.map(({ n, title, body }) => (
            <div key={n} className="rounded-xl bg-[#121824] border border-white/10 p-5 flex gap-4">
              <div className="font-black text-4xl text-emerald-400/80 leading-none" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{n}</div>
              <div>
                <div className="text-lg font-bold uppercase tracking-wide text-white" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{title}</div>
                <p className="text-sm text-slate-400 mt-1">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------- Discord band ---------- */
function DiscordBand() {
  return (
    <section className="px-4 sm:px-6 py-16 border-t border-white/5 bg-[#0C111C]">
      <div className="max-w-5xl mx-auto grid md:grid-cols-[1fr_auto] items-center gap-6">
        <div>
          <h2 className="text-xs uppercase tracking-widest text-emerald-400 font-semibold">Discord-first alerts</h2>
          <h3 className="mt-2 text-3xl sm:text-4xl font-black uppercase tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            Your group chat finds out before you do.
          </h3>
          <p className="mt-3 text-slate-400 max-w-2xl">
            One webhook, every leg move, with your display name on it — never your email. Mute slips you
            don't want blowing up chat, keep your biggest tickets loud.
          </p>
        </div>
        <div className="rounded-xl bg-[#121824] border border-white/10 p-4 min-w-[300px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">#bets · BetTrackr</div>
          <div className="text-sm text-white font-semibold">👤 Dave  |  🎯 James Cook Over 11.5 Rec Yds  |  📊 9 / 11.5  |  🟢 ON TRACK</div>
          <div className="mt-2 text-xs text-slate-500">Just now · delivered</div>
        </div>
      </div>
    </section>
  );
}

/* ---------- Final CTA ---------- */
function FinalCTA() {
  return (
    <section className="px-4 sm:px-6 py-20 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none" style={{
        background: "radial-gradient(ellipse at center, rgba(16,185,129,0.12), transparent 60%)",
      }} />
      <div className="relative max-w-3xl mx-auto text-center">
        <Zap className="h-8 w-8 text-emerald-400 mx-auto mb-4" />
        <h2 className="text-4xl sm:text-5xl font-black uppercase tracking-tight" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
          Ready for <span className="text-emerald-400">kickoff</span>?
        </h2>
        <p className="mt-3 text-slate-400">
          Free to use. Snap your first slip in under 60 seconds.
        </p>
        <div className="mt-7 flex items-center justify-center gap-3">
          <Link to="/login" data-testid="welcome-final-signup">
            <Button className="bg-emerald-500 hover:bg-emerald-600 text-slate-900 font-bold uppercase tracking-wider h-12 px-8">
              Create your account <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </Link>
          <Link to="/login" data-testid="welcome-final-login" className="text-sm uppercase tracking-widest text-slate-300 hover:text-white">
            I already have one
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="px-4 sm:px-6 py-10 border-t border-white/10">
      <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
        <div className="flex items-center gap-2">
          <img src="/bettrackr-logo.svg" alt="" className="h-5 w-5 opacity-80" />
          <span className="uppercase tracking-widest">BetTrackr · NFL Betting Tracker</span>
        </div>
        <div className="flex items-center gap-5">
          <Link to="/welcome" className="hover:text-white">Home</Link>
          <Link to="/login" className="hover:text-white">Log in</Link>
          <a href="#features" className="hover:text-white">Features</a>
        </div>
      </div>
    </footer>
  );
}
